/**
 * Permission Constants
 * 
 * These constants map to the actual permission names returned by the backend API.
 * All permissions are fetched dynamically from the backend, but these constants
 * provide type safety and prevent typos when checking permissions.
 * 
 * To get the actual permissions list, use:
 * - useGetAllPermissions() hook
 * - useGetRolePermissions(roleId) hook
 * - useGetPermissions(userRoles) hook
 */

export const PERMISSION_NAMES = {
    // Admin Management
    GET_ADMIN_USERS: 'get admin users (admin)',
    GET_ADMIN_USER: 'get admin user (admin)',
    MODIFY_ADMIN_USER: 'modify admin user (admin)',
    
    // Role Management
    GET_ROLES: 'get roles (admin)',
    GET_ROLE: 'get role (admin)',
    GET_ROLE_PERMISSIONS: 'get role permissions (admin)',
    GET_USER_ROLES: 'get user roles (admin)',
    
    // Permission Management
    GET_PERMISSIONS: 'get permissions (admin)',
    GET_PERMISSION: 'get permission (admin)',
    
    // User Management
    GET_USERS: 'get all users (admin)',
    GET_INDIVIDUAL_USERS: 'get individual users (admin)',
    GET_INDIVIDUAL_USER: 'get individual user (admin)',
    GET_BUSINESS_USERS: 'get business users (admin)',
    GET_BUSINESS_USER: 'get business user (admin)',
    MODIFY_USER: 'modify user (admin)',
    MODIFY_BUSINESS_USER: 'modify business user (admin)',
    
    // Transaction Management
    GET_TRANSACTIONS: 'get transactions (admin)',
    GET_TRANSACTION: 'get transaction (admin)',
    MODIFY_TRANSACTION: 'modify transaction (admin)',
    
    // Card Management
    GET_USER_CARDS: 'get user cards (admin)',
    GET_USER_CARD: 'get user card (admin)',
    REGISTER_PHYSICAL_CARD: 'register physical card (admin)',
    
    // Currency Management
    GET_CURRENCIES: 'get currencies (admin)',
    GET_CURRENCY: 'get currency (admin)',
    
    // Exchange Rate Management
    GET_EXCHANGE_RATES: 'get exchange rates (admin)',
    GET_EXCHANGE_RATE: 'get exchange rate (admin)',
    GET_EXCHANGE_RATE_BANDS: 'get exchange rate bands (admin)',
    GET_EXCHANGE_RATE_BAND: 'get exchange rate band (admin)',
    
    // Fee Management
    GET_FEES: 'get fees (admin)',
    GET_FEE: 'get fee (admin)',
    GET_FEE_BANDS: 'get fee bands (admin)',
    GET_FEE_BAND: 'get fee band (admin)',
    
    // Limits Management
    GET_LIMITS: 'get limits (admin)',
    GET_LIMIT: 'get limit (admin)',
    
    // Audit Logs
    GET_AUDIT_LOGS: 'get audit logs (admin)',
    GET_AUDIT_LOG: 'get audit log (admin)',
    
    // Analytics
    GET_ANALYTICS: 'get analytics (admin)',
    
    // Compliance
    MODIFY_COMPLIANCE_STATUS: 'modify compliance status (admin)',
    GET_KYC_CONFIGURATION: 'get kyc configuration (admin)',
    GET_COMPLIANCE_RECORDS: 'get compliance records (admin)',
    
    // Discounts
    GET_DISCOUNTS: 'get discounts (admin)',
    GET_DISCOUNT: 'get discount (admin)',
    
    // Issuers
    GET_ISSUERS: 'get issuers (admin)',
    GET_ISSUER: 'get issuer (admin)',
    
    // Pickup Locations
    GET_PICKUP_LOCATIONS: 'get pickup locations (admin)',
    GET_PICKUP_LOCATION: 'get pickup location (admin)',
    
    // Settings
    GET_SETTINGS: 'get settings (admin)',
    GET_SETTING: 'get setting (admin)',
    GET_SETTING_VALUES: 'get setting values (admin)',
    GET_SETTING_VALUE: 'get setting value (admin)',
    CREATE_SETTING_VALUE: 'create setting value (admin)',
    MODIFY_SETTING_VALUE: 'modify setting value (admin)',
    REMOVE_SETTING_VALUE: 'remove setting value (admin)',
} as const;

// Type for permission names
export type PermissionName = typeof PERMISSION_NAMES[keyof typeof PERMISSION_NAMES];

/**
 * Helper function to check if a permission name is valid
 */
export const isValidPermissionName = (name: string): name is PermissionName => {
    return Object.values(PERMISSION_NAMES).includes(name as PermissionName);
};
